/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { StructureProperties, StructureElement, Bond, Model } from '../../mol-model/structure';
import { Color } from '../../mol-util/color';
import { ParamDefinition as PD } from '../../mol-util/param-definition';
import { getPaletteParams, getPalette } from '../../mol-util/color/palette';
var DefaultList = 'many-distinct';
var DefaultColor = Color(0xFAFAFA);
var Description = 'Gives every chain a color based on its `asym_id` value.';
export var ChainIdColorThemeParams = __assign({ asymId: PD.Select('auth', PD.arrayToOptions(['auth', 'label'])) }, getPaletteParams({ type: 'colors', colorList: DefaultList }));
export function getChainIdColorThemeParams(ctx) {
    var _a;
    var params = PD.clone(ChainIdColorThemeParams);
    if ((_a = ctx.structure) === null || _a === void 0 ? void 0 : _a.models.some(function (m) { return m.coarseHierarchy.isDefined; })) {
        params.asymId.defaultValue = 'label';
    }
    return params;
}
function getAsymId(unit, type) {
    switch (unit.kind) {
        case 0 /* Atomic */:
            return type === 'auth'
                ? StructureProperties.chain.auth_asym_id
                : StructureProperties.chain.label_asym_id;
        case 1 /* Spheres */:
        case 2 /* Gaussians */:
            return StructureProperties.coarse.asym_id;
    }
}
function getAsymIdKey(location, type) {
    var asymId = getAsymId(location.unit, type)(location);
    return location.structure.root.models.length > 1
        ? getKey(location.unit.model, asymId)
        : asymId;
}
function getKey(model, asymId) {
    return asymId + "|" + ((Model.Index.get(model).value || 0) + 1);
}
function getAsymIdSerialMap(structure, type) {
    var map = new Map();
    var _loop_1 = function (m) {
        var asymIdOffset = Model.AsymIdOffset.get(m).value;
        var offset = (type === 'auth' ? asymIdOffset === null || asymIdOffset === void 0 ? void 0 : asymIdOffset.auth : asymIdOffset === null || asymIdOffset === void 0 ? void 0 : asymIdOffset.label) || 0;
        var count = 0;
        m.properties.structAsymMap.forEach(function (_a, label_id) {
            var auth_id = _a.auth_id;
            var asymId = type === 'auth' ? auth_id : label_id;
            var k = structure.models.length > 1
                ? getKey(m, asymId)
                : asymId;
            if (!map.has(k)) {
                map.set(k, count + offset);
                ++count;
            }
        });
    };
    for (var _i = 0, _a = structure.models; _i < _a.length; _i++) {
        var m = _a[_i];
        _loop_1(m);
    }
    return map;
}
export function ChainIdColorTheme(ctx, props) {
    var color;
    var legend;
    if (ctx.structure) {
        var l_1 = StructureElement.Location.create(ctx.structure.root);
        var asymIdSerialMap_1 = getAsymIdSerialMap(ctx.structure.root, props.asymId);
        var labelTable_1 = Array.from(asymIdSerialMap_1.keys());
        var valueLabel = function (i) { return labelTable_1[i]; };
        var palette_1 = getPalette(asymIdSerialMap_1.size, props, { valueLabel: valueLabel });
        legend = palette_1.legend;
        color = function (location) {
            var serial = undefined;
            if (StructureElement.Location.is(location)) {
                var k = getAsymIdKey(location, props.asymId);
                serial = asymIdSerialMap_1.get(k);
            }
            else if (Bond.isLocation(location)) {
                l_1.unit = location.aUnit;
                l_1.element = location.aUnit.elements[location.aIndex];
                var k = getAsymIdKey(l_1, props.asymId);
                serial = asymIdSerialMap_1.get(k);
            }
            return serial === undefined ? DefaultColor : palette_1.color(serial);
        };
    }
    else {
        color = function () { return DefaultColor; };
    }
    return {
        factory: ChainIdColorTheme,
        granularity: 'group',
        color: color,
        props: props,
        description: Description,
        legend: legend
    };
}
export var ChainIdColorThemeProvider = {
    name: 'chain-id',
    label: 'Chain Id',
    category: "Chain Property" /* Chain */,
    factory: ChainIdColorTheme,
    getParams: getChainIdColorThemeParams,
    defaultValues: PD.getDefaultValues(ChainIdColorThemeParams),
    isApplicable: function (ctx) { return !!ctx.structure; }
};
//# sourceMappingURL=chain-id.js.map